import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as api from '../api/client'
import { useDecisionStore } from '../store/decision'
import type { ValidationResponse, WSMessage } from '../types'
import Dashboard from '../components/Dashboard'
import ActivityDeck from '../components/ActivityDeck'
import DecisionComposer from '../components/DecisionComposer'
import EventsPanel from '../components/EventsPanel'
import TrajectoryPanel from '../components/TrajectoryPanel'
import QuarterClock from '../components/QuarterClock'
import GameRecap from '../components/GameRecap'

type Tab = 'play' | 'trajectory'

function useTeamViewTab(sessionId: string, teamId: string): [Tab, (t: Tab) => void] {
  const storageKey = `team-view-tab:${sessionId}:${teamId}`
  const [tab, setTabState] = useState<Tab>(() => {
    if (typeof window === 'undefined') return 'play'
    const stored = window.localStorage.getItem(storageKey)
    return stored === 'trajectory' ? 'trajectory' : 'play'
  })
  const setTab = (t: Tab) => {
    setTabState(t)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(storageKey, t)
    }
  }
  return [tab, setTab]
}

export default function TeamView() {
  const { sessionId = '', teamId = '' } = useParams()
  const qc = useQueryClient()
  const [tab, setTab] = useTeamViewTab(sessionId, teamId)

  const dashQ = useQuery({
    queryKey: ['dashboard', sessionId, teamId],
    queryFn: () => api.getDashboard(sessionId, teamId),
    refetchInterval: 10_000,
  })

  const catQ = useQuery({
    queryKey: ['team-catalog', sessionId, teamId],
    queryFn: () => api.getTeamCatalog(sessionId, teamId),
    refetchInterval: 10_000,
  })

  const sessQ = useQuery({
    queryKey: ['session', sessionId],
    queryFn: () => api.getSession(sessionId),
    refetchInterval: 5_000,
  })

  // WebSocket: auto-reconnect simple version.
  const wsRef = useRef<WebSocket | null>(null)
  useEffect(() => {
    if (!sessionId || !teamId) return
    const handle = (msg: WSMessage) => {
      if (msg.type === 'quarter_advanced') {
        qc.invalidateQueries({ queryKey: ['dashboard', sessionId, teamId] })
        qc.invalidateQueries({ queryKey: ['team-catalog', sessionId, teamId] })
        qc.invalidateQueries({ queryKey: ['session', sessionId] })
        qc.invalidateQueries({ queryKey: ['session-events', sessionId] })
        qc.invalidateQueries({ queryKey: ['session-history', sessionId] })
        useDecisionStore.getState().reset()
      } else if (msg.type === 'timer_updated') {
        qc.invalidateQueries({ queryKey: ['session', sessionId] })
      }
    }
    const ws = api.openTeamWebSocket(sessionId, teamId, handle)
    wsRef.current = ws
    return () => ws.close()
  }, [sessionId, teamId, qc])

  const draft = useDecisionStore((s) => s.draft)
  const reset = useDecisionStore((s) => s.reset)

  const [validation, setValidation] = useState<ValidationResponse | null>(null)
  const submitMut = useMutation({
    mutationFn: () => api.submitDecision(sessionId, teamId, draft),
    onSuccess: (v) => {
      setValidation(v)
      if (v.valid) {
        qc.invalidateQueries({ queryKey: ['dashboard', sessionId, teamId] })
      }
    },
  })

  if (dashQ.isLoading || catQ.isLoading || sessQ.isLoading) {
    return <div className="p-8 text-slate-500">Loading…</div>
  }
  if (dashQ.isError || !dashQ.data) {
    return <div className="p-8 text-danger">Failed to load team state.</div>
  }

  const dash = dashQ.data
  const cat = catQ.data ?? []
  const session = sessQ.data
  const isWaiting = dash.pending_decision_submitted && session?.status === 'active'
  const sessionComplete = session?.status === 'complete'

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-20">
        <div className="max-w-screen-2xl mx-auto px-6 pt-3 pb-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold text-brand-700">
                AI Transformation Flight Simulator
              </h1>
              <p className="text-sm text-slate-500">
                {session?.name} · Q{Math.min(dash.quarter + 1, session?.num_quarters ?? dash.quarter + 1)} / {session?.num_quarters}
                {sessionComplete && <span className="ml-2 text-ok font-medium">COMPLETE</span>}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {session && <QuarterClock session={session} />}
              {isWaiting && (
                <div className="rounded bg-amber-100 px-3 py-1 text-sm text-amber-800">
                  Decision submitted — waiting for facilitator to advance
                </div>
              )}
              <EventsPanel sessionId={sessionId} teamId={teamId} />
            </div>
          </div>

          <TabStrip tab={tab} setTab={setTab} />
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto px-6 py-6">
        {tab === 'play' ? (
          <div className="grid grid-cols-12 gap-6">
            <aside className="col-span-12 lg:col-span-3">
              <Dashboard dashboard={dash} sessionId={sessionId} teamId={teamId} />
            </aside>

            <section className="col-span-12 lg:col-span-6">
              <ActivityDeck
                catalog={cat}
                oneshotCounters={dash.consecutive_oneshot_firings}
              />
            </section>

            <aside className="col-span-12 lg:col-span-3">
              {sessionComplete ? (
                <GameRecap
                  sessionId={sessionId}
                  teamId={teamId}
                  catalog={cat}
                />
              ) : (
                <DecisionComposer
                  catalog={cat}
                  dashboard={dash}
                  onValidate={() => submitMut.mutate()}
                  onSubmit={() => submitMut.mutate()}
                  onReset={() => { reset(); setValidation(null) }}
                  validation={validation}
                  submitting={submitMut.isPending}
                  pendingForcedActivity={dash.pending_forced_activity}
                  alreadySubmitted={isWaiting}
                  sessionActive={session?.status === 'active'}
                />
              )}
            </aside>
          </div>
        ) : (
          <TrajectoryPanel sessionId={sessionId} teamId={teamId} catalog={cat} />
        )}
      </main>
    </div>
  )
}

function TabStrip({ tab, setTab }: { tab: Tab; setTab: (t: Tab) => void }) {
  return (
    <div className="flex gap-1 mt-3 -mb-px" role="tablist">
      <TabButton active={tab === 'play'} onClick={() => setTab('play')}>
        Play
      </TabButton>
      <TabButton active={tab === 'trajectory'} onClick={() => setTab('trajectory')}>
        Trajectory
      </TabButton>
    </div>
  )
}

function TabButton({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={
        'px-4 py-2 text-sm font-medium border-b-2 transition-colors ' +
        (active
          ? 'border-brand-600 text-brand-700'
          : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300')
      }
    >
      {children}
    </button>
  )
}
